/**
 * Movements Page (Prima Nota)
 *
 * Journal entries and general ledger movements management.
 * Uses accounting store to fetch data from database via GraphQL.
 */

import React, { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from '@business-assistant/shared/hooks';
import type { Movement } from '@business-assistant/shared/data/types';
import type { CentsAmount } from '@business-assistant/shared';
import { MovementForm } from '../forms';
import type { MovementFormData } from '../forms';
import { FastActions, type ActionType, type LinkedCounts } from '../components/FastActions';
import { useAccountingStore } from '../../state/accountingState';
import type { Movement as StoreMovement } from '../../types/core';
import '../accounting.css';

// =============================================================================
// TYPES
// =============================================================================

interface MovementsPageProps {
  onNavigate?: (path: string) => void;
  onBack?: () => void;
}

type ViewMode = 'list' | 'create' | 'edit';

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

function formatCurrency(cents: CentsAmount): string {
  return new Intl.NumberFormat('it-IT', {
    style: 'currency',
    currency: 'EUR',
  }).format(cents / 100);
}

function formatDate(date: string): string {
  return new Date(date).toLocaleDateString('it-IT', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

// =============================================================================
// COMPONENTS
// =============================================================================

interface MovementRowProps {
  movement: Movement;
  /** Linked items count per action type */
  linkedCounts?: LinkedCounts;
  onClick?: (movement: Movement) => void;
  onActionClick?: (actionType: ActionType, movement: Movement) => void;
}

function MovementRow({
  movement,
  linkedCounts = {},
  onClick,
  onActionClick,
}: MovementRowProps) {
  const isIn = movement.direction === 'in';

  // Available actions for movements
  const availableActions: ActionType[] = ['invoice', 'file', 'note', 'task'];

  return (
    <div
      className="accounting-card--row"
      onClick={() => onClick?.(movement)}
      style={{ cursor: onClick ? 'pointer' : 'default' }}
    >
      {/* Direction Icon */}
      <div
        className="accounting-icon--sm accounting-flex--center"
        style={{ color: isIn ? '#16a34a' : '#dc2626' }}
      >
        <svg
          width="20"
          height="20"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d={isIn ? 'M19 14l-7 7m0 0l-7-7m7 7V3' : 'M5 10l7-7m0 0l7 7m-7-7v18'}
          />
        </svg>
      </div>

      {/* Info */}
      <div className="accounting-content" style={{ flex: 1 }}>
        <div style={{ fontWeight: 500 }}>{movement.description}</div>
        <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
          {formatDate(movement.date)} | {movement.category}
          {movement.account && ` | ${movement.account}`}
        </div>
      </div>

      {/* Amount */}
      <div style={{
        fontWeight: 600,
        fontSize: '1rem',
        color: isIn ? '#16a34a' : '#dc2626',
        marginRight: '1rem',
      }}>
        {isIn ? '+' : '-'}{formatCurrency(movement.amount)}
      </div>

      {/* Fast Actions with counter badges */}
      <div onClick={(e) => e.stopPropagation()}>
        <FastActions
          entityId={movement.id}
          entityName={movement.description}
          actions={availableActions}
          linkedCounts={linkedCounts}
          onActionClick={(actionType) => onActionClick?.(actionType, movement)}
        />
      </div>
    </div>
  );
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export function MovementsPage({ onNavigate, onBack }: MovementsPageProps) {
  const { t } = useTranslation();

  // URL parameter management
  const { params, setParam, clearParam, hasActiveFilters } = useSearchParams();

  // Get movements from store (fetched from database via GraphQL)
  const {
    movements: storeMovements,
    loadMovements,
    loading,
  } = useAccountingStore();

  // Load movements on mount
  useEffect(() => {
    loadMovements();
  }, [loadMovements]);

  // Map store movements to the Movement type used by this component
  const movements: Movement[] = storeMovements.map((mov: StoreMovement) => ({
    id: mov.id,
    date: mov.date,
    description: mov.description,
    category: mov.category,
    amount: mov.amount,
    direction: mov.direction,
    account: mov.account,
    invoiceId: mov.invoiceId,
    projectId: mov.projectId,
    notes: mov.notes,
    createdAt: mov.createdAt,
    updatedAt: mov.updatedAt,
  }));

  // Local state
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [selectedMovement, setSelectedMovement] = useState<Movement | null>(null);

  // Handle search input - updates URL
  const handleSearch = useCallback((query: string) => {
    if (query.trim()) {
      setParam('s', query);
    } else {
      clearParam('s');
    }
  }, [setParam, clearParam]);

  // Handle direction filter - updates URL
  const handleDirectionFilter = useCallback((direction: string) => {
    if (direction === 'all') {
      clearParam('direction');
    } else {
      setParam('direction', direction);
    }
  }, [setParam, clearParam]);

  // Filter movements based on URL parameters
  const filteredMovements = movements.filter((m) => {
    const directionFilter = params.direction || 'all';
    const searchQuery = params.s || '';

    const matchesDirection = directionFilter === 'all' || m.direction === directionFilter;
    const matchesSearch =
      !searchQuery ||
      m.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
      m.category.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesDirection && matchesSearch;
  });

  const totalIn = movements.filter((m) => m.direction === 'in').reduce((sum, m) => sum + m.amount, 0);
  const totalOut = movements.filter((m) => m.direction === 'out').reduce((sum, m) => sum + m.amount, 0);

  return (
    <div className="accounting-page accounting-page--narrow">
      {/* Stats */}
      <div className="accounting-grid--stats">
        <div className="accounting-stat">
          <div>{t('accounting.dashboard.income')}</div>
          <div>+{formatCurrency(totalIn)}</div>
        </div>
        <div className="accounting-stat">
          <div>{t('accounting.dashboard.expenses')}</div>
          <div>-{formatCurrency(totalOut)}</div>
        </div>
        <div className="accounting-stat">
          <div>{t('accounting.dashboard.balance')}</div>
          <div>{formatCurrency(totalIn - totalOut)}</div>
        </div>
      </div>

      {/* Toolbar */}
      <div className="accounting-toolbar">
        <div className="accounting-toolbar__search">
          <input
            type="text"
            placeholder={t('common.search')}
            defaultValue={params.s || ''}
            onChange={(e) => handleSearch(e.target.value)}
          />
        </div>
        <select
          value={params.direction || 'all'}
          onChange={(e) => handleDirectionFilter(e.target.value)}
        >
          <option value="all">{t('common.all')}</option>
          <option value="in">{t('accounting.dashboard.income')}</option>
          <option value="out">{t('accounting.dashboard.expenses')}</option>
        </select>
        {hasActiveFilters && (
          <button
            onClick={() => {
              clearParam('s');
              clearParam('direction');
            }}
          >
            {t('common.clearFilters')}
          </button>
        )}
        <button
          onClick={() => setViewMode('create')}
          className="accounting-flex--row accounting-flex--gap-sm"
        >
          <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          {t('accounting.dashboard.newMovement')}
        </button>
      </div>

      {/* Movement List */}
      {filteredMovements.length === 0 ? (
        <div className="accounting-empty">
          <svg
            width="48"
            height="48"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4"
            />
          </svg>
          <p>Nessun movimento trovato</p>
        </div>
      ) : (
        <div className="accounting-list">
          {filteredMovements.map((movement) => {
            // TODO: Fetch linked counts from LinkManager when available
            const linkedCounts: LinkedCounts = {};

            return (
              <MovementRow
                key={movement.id}
                movement={movement}
                linkedCounts={linkedCounts}
                onClick={(m) => {
                  setSelectedMovement(m);
                  setViewMode('edit');
                }}
                onActionClick={(actionType, m) => {
                  console.log('Fast action clicked:', actionType, 'on movement:', m.id);
                  // TODO: Open LinkedActionsPanel for this action type
                }}
              />
            );
          })}
        </div>
      )}

      {/* Create Movement Form */}
      {viewMode === 'create' && (
        <MovementForm
          onSave={async (data: MovementFormData) => {
            // Create via store (saves to database)
            const { createMovement } = useAccountingStore.getState();
            await createMovement({
              date: data.date,
              description: data.description,
              amount: Math.round(data.amount * 100) as CentsAmount,
              direction: data.direction,
              category: data.category,
              account: data.account,
            });
            setViewMode('list');
          }}
          onCancel={() => setViewMode('list')}
        />
      )}

      {/* Edit Movement Form */}
      {viewMode === 'edit' && selectedMovement && (
        <MovementForm
          movement={selectedMovement}
          onSave={async (data: MovementFormData) => {
            // Update via store (saves to database)
            const { updateMovement } = useAccountingStore.getState();
            await updateMovement(selectedMovement.id, {
              date: data.date,
              description: data.description,
              amount: Math.round(data.amount * 100) as CentsAmount,
              direction: data.direction,
              category: data.category,
              account: data.account,
            });
            setSelectedMovement(null);
            setViewMode('list');
          }}
          onCancel={() => {
            setSelectedMovement(null);
            setViewMode('list');
          }}
        />
      )}
    </div>
  );
}

export type { MovementsPageProps };
